<template>
    <Card :title="$t('cardTitles.orderHistory')">
        <div class="modal-body">
            <div class="list-feed">
                <div v-for="log in formattedLogs" :key="log.id" class="list-feed-item">
                    <div class="text-muted">{{ DateFormatter.formatDateTime(log.date) }}</div>

                    <span v-html="log.title"></span>

                    <div v-if="log.hasChanges" class="card card-body bg-light mb-0 mt-2">
                        <dl class="row mb-0">
                            <template v-for="(change, index) in log.changesList" :key="index">
                                <dt class="col-sm-3">{{ change.label }}</dt>
                                <dd class="col-sm-9" v-html="change.value"></dd>
                            </template>
                        </dl>
                    </div>
                </div>
            </div>
        </div>
    </Card>
</template>
<script>
import Card from "../../../../../components/new/CardComponent.vue";

import { httpClient } from "../../../../../services/HttpClient";
import DateFormatter from "../../../../../services/DateFormatter";
import { privateChannel } from "../../../../services/echoService";
import { mapGetters } from "vuex";

const NOTIFICATION_CREATED_EVENT = '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated';

const MARKET_PLACE_NOTIFICATION_TYPES = [
    'App\\Modules\\Integration\\Notifications\\SetDeliveryNumberOnMarketPlaceDispatchedNotification',
    'App\\Modules\\Integration\\Notifications\\SetDeliveryNumberOnMarketPlaceSuccessNotification',
    'App\\Modules\\Integration\\Notifications\\SetDeliveryNumberOnMarketPlaceFailedNotification',
    'App\\Modules\\Integration\\Notifications\\ApproveOrderOnMarketPlaceDispatchedNotification',
    'App\\Modules\\Integration\\Notifications\\ApproveOrderOnMarketPlaceSuccessNotification',
    'App\\Modules\\Integration\\Notifications\\ApproveOrderOnMarketPlaceFailedNotification',
    'App\\Modules\\Integration\\Notifications\\ResyncOrderFromMarketPlaceDispatchedNotification',
    'App\\Modules\\Integration\\Notifications\\ResyncOrderFromMarketPlaceSuccessNotification',
    'App\\Modules\\Integration\\Notifications\\ResyncOrderFromMarketPlaceFailedNotification',
];

const SET_TTN_ERROR_TRANSLATION_KEYS = {
    'This order has no delivery, so there is no tracking number to send.': 'alerts.set_ttn_error_delivery_missing',
    'This delivery has no tracking number yet.': 'alerts.set_ttn_error_delivery_number_missing',
    'The marketplace already has a tracking number for this order.': 'alerts.set_ttn_error_delivery_number_already_set',
    'This marketplace does not accept tracking numbers.': 'alerts.set_ttn_error_service_unsupported',
    'This integration has no API credentials.': 'alerts.set_ttn_error_credentials_missing',
    'The API credentials of this integration are not active.': 'alerts.set_ttn_error_credentials_inactive',
    'The current order status on the marketplace does not allow sending a tracking number.': 'alerts.set_ttn_error_source_status_not_allowed',
    'The marketplace does not accept a tracking number for this delivery method.': 'alerts.set_ttn_error_delivery_method_unsupported',
};

const SET_TTN_ATTEMPT_ERROR_TRANSLATION_KEYS = {
    'The marketplace did not accept the API credentials.': 'alerts.set_ttn_attempt_error_credentials_rejected',
    'The marketplace does not allow this account to change the order.': 'alerts.set_ttn_attempt_error_account_forbidden',
    'The tracking number cannot be attached to this order.': 'alerts.set_ttn_attempt_error_delivery_number_not_allowed',
    'The marketplace does not allow the order to move into the status the tracking number is sent with.': 'alerts.set_ttn_attempt_error_status_transition_not_allowed',
    'The marketplace did not accept the tracking number.': 'alerts.set_ttn_attempt_error_invalid_delivery_number',
    'The marketplace refused the request for an unknown reason.': 'alerts.set_ttn_attempt_error_unknown_refusal',
    'The marketplace refused the request.': 'alerts.set_ttn_attempt_error_request_refused',
    'The marketplace refused the token request.': 'alerts.set_ttn_attempt_error_token_refused',
    'The marketplace answered with an unexpected HTTP status.': 'alerts.set_ttn_attempt_error_unexpected_http_status',
    'The marketplace did not respond.': 'alerts.set_ttn_attempt_error_no_response',
    'An internal error occurred while sending the tracking number.': 'alerts.set_ttn_attempt_error_internal',
};

const APPROVE_ORDER_ERROR_TRANSLATION_KEYS = {
    'This marketplace does not accept an order approve.': 'alerts.approve_order_error_service_unsupported',
    'This integration has no API credentials.': 'alerts.approve_order_error_credentials_missing',
    'The API credentials of this integration are not active.': 'alerts.approve_order_error_credentials_inactive',
    'The current order status on the marketplace does not allow an approve.': 'alerts.approve_order_error_source_status_not_allowed',
};

const APPROVE_ORDER_ATTEMPT_ERROR_TRANSLATION_KEYS = {
    'The marketplace did not accept the API credentials.': 'alerts.approve_order_attempt_error_credentials_rejected',
    'The marketplace does not allow this account to change the order.': 'alerts.approve_order_attempt_error_account_forbidden',
    'The marketplace does not know this order.': 'alerts.approve_order_attempt_error_order_not_found',
    'The marketplace does not allow the order to move into the approved status.': 'alerts.approve_order_attempt_error_status_transition_not_allowed',
    'The marketplace refused the request.': 'alerts.approve_order_attempt_error_request_refused',
    'The marketplace refused the request for an unknown reason.': 'alerts.approve_order_attempt_error_unknown_refusal',
    'The marketplace answered with an unexpected HTTP status.': 'alerts.approve_order_attempt_error_unexpected_http_status',
    'The marketplace did not respond.': 'alerts.approve_order_attempt_error_no_response',
    'An internal error occurred while approving the order.': 'alerts.approve_order_attempt_error_internal',
};

const RESYNC_ORDER_ERROR_TRANSLATION_KEYS = {
    'This marketplace does not accept an order resync.': 'alerts.resync_order_error_service_unsupported',
    'This integration has no API credentials.': 'alerts.resync_order_error_credentials_missing',
    'The API credentials of this integration are not active.': 'alerts.resync_order_error_credentials_inactive',
};

const RESYNC_ORDER_ATTEMPT_ERROR_TRANSLATION_KEYS = {
    'The marketplace did not accept the API credentials.': 'alerts.resync_order_attempt_error_credentials_rejected',
    'The marketplace does not allow this account to read the order.': 'alerts.resync_order_attempt_error_account_forbidden',
    'The marketplace does not know this order.': 'alerts.resync_order_attempt_error_order_not_found',
    'The marketplace refused the request.': 'alerts.resync_order_attempt_error_request_refused',
    'The marketplace refused the request for an unknown reason.': 'alerts.resync_order_attempt_error_unknown_refusal',
    'The marketplace refused the token request.': 'alerts.resync_order_attempt_error_token_refused',
    'The marketplace answered with an unexpected HTTP status.': 'alerts.resync_order_attempt_error_unexpected_http_status',
    'The marketplace did not respond.': 'alerts.resync_order_attempt_error_no_response',
    'An internal error occurred while resyncing the order.': 'alerts.resync_order_attempt_error_internal',
};

const ERROR_TRANSLATION_KEYS_BY_EVENT_PREFIX = {
    'integration_order_delivery_number_': SET_TTN_ERROR_TRANSLATION_KEYS,
    'integration_order_approve_': APPROVE_ORDER_ERROR_TRANSLATION_KEYS,
    'integration_order_resync_': RESYNC_ORDER_ERROR_TRANSLATION_KEYS,
};

const ATTEMPT_ERROR_TRANSLATION_KEYS_BY_EVENT_PREFIX = {
    'integration_order_delivery_number_': SET_TTN_ATTEMPT_ERROR_TRANSLATION_KEYS,
    'integration_order_approve_': APPROVE_ORDER_ATTEMPT_ERROR_TRANSLATION_KEYS,
    'integration_order_resync_': RESYNC_ORDER_ATTEMPT_ERROR_TRANSLATION_KEYS,
};

export default {
    name: "OrderHistoryCard",
    components: {
        Card
    },
    props: {
        order: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            DateFormatter,
            logs: [],
            clientNameByUuid: {},
            statusNameByUuid: {},
            sourceNameByUuid: {},
            integrationStatusNameByUuid: {},
            notificationChannel: null
        }
    },
    methods: {
        subscribeToNotifications(profileId) {
            if (!profileId || this.notificationChannel) {
                return;
            }

            this.notificationChannel = privateChannel(`projects.${this.project.id}.users.${profileId}`)
                .notification(this.onNotificationReceived);
        },
        unsubscribeFromNotifications() {
            if (!this.notificationChannel) {
                return;
            }

            this.notificationChannel.stopListening(NOTIFICATION_CREATED_EVENT, this.onNotificationReceived);

            this.notificationChannel = null;
        },
        onNotificationReceived(notification) {
            if (!MARKET_PLACE_NOTIFICATION_TYPES.includes(notification.type)) {
                return;
            }

            const integrationOrderUuid = notification.additional_data?.integration_order?.uuid;

            if (!integrationOrderUuid || integrationOrderUuid !== this.order.integration_order?.uuid) {
                return;
            }

            this.fetchLogs();
        },
        errorTranslationKeys(event, keysByEventPrefix) {
            const prefix = Object.keys(keysByEventPrefix).find(prefix => event.startsWith(prefix));

            return keysByEventPrefix[prefix] ?? {};
        },
        logProperties(log) {
            return Array.isArray(log.properties) ? {} : log.properties;
        },
        fetchLogs() {
            httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.$route.params.orderUuid}/activities`)
                .then(async response => {
                    if (response.data && response.data.data) {
                        let clientUuids = [];
                        let statusUuids = [];
                        let sourceUuids = [];
                        let integrationStatusUuids = [];

                        response.data.data.forEach(log => {
                            const properties = this.logProperties(log);
                            const old = properties.old ?? {};
                            const attributes = properties.attributes ?? {};

                            if (log.subject.type === 'Order') {
                                if (old.client_uuid) {
                                    clientUuids.push(old.client_uuid);
                                }

                                if (attributes.client_uuid) {
                                    clientUuids.push(attributes.client_uuid);
                                }

                                if (old.status_uuid) {
                                    statusUuids.push(old.status_uuid);
                                }

                                if (attributes.status_uuid) {
                                    statusUuids.push(attributes.status_uuid);
                                }

                                if (old.source_uuid) {
                                    sourceUuids.push(old.source_uuid);
                                }

                                if (attributes.source_uuid) {
                                    sourceUuids.push(attributes.source_uuid);
                                }
                            }

                            if (log.event === 'integration_order_created' || log.event === 'integration_order_updated') {
                                if (old.status_uuid) {
                                    integrationStatusUuids.push(old.status_uuid);
                                }

                                if (attributes.status_uuid) {
                                    integrationStatusUuids.push(attributes.status_uuid);
                                }
                            }
                        })

                        if (clientUuids.length > 0) {
                            await this.fetchClientName(clientUuids);
                        }

                        if (statusUuids.length > 0) {
                            await this.fetchStatusName(statusUuids);
                        }

                        if (sourceUuids.length > 0) {
                            await this.fetchSourceName(sourceUuids);
                        }

                        if (integrationStatusUuids.length > 0 && this.order.integration_order?.integration?.uuid) {
                            await this.fetchIntegrationStatusNames(this.order.integration_order.integration.uuid);
                        }
                    }

                    this.logs = response.data.data;
                })
        },
        async fetchClientName(clientUuids) {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/clients`,
                {
                    uuids: clientUuids
                }
            ).then(response => {
                const clients = response.data.data;

                clients.forEach(client => {
                    this.clientNameByUuid[client.uuid] = client.name;
                });
            })
        },
        async fetchStatusName(statusUuids) {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/statuses`,
                {
                    uuids: statusUuids,
                    orders: [
                        {
                            column: 'priority_order',
                            dir: 'asc'
                        }
                    ]
                }
            ).then(response => {
                const statuses = response.data.data;

                statuses.forEach(status => {
                    this.statusNameByUuid[status.uuid] = status.name;
                });
            })
        },
        async fetchSourceName(sourceUuids) {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/sources`,
                {
                    uuids: sourceUuids
                }
            ).then(response => {
                const sources = response.data.data;

                sources.forEach(source => {
                    this.sourceNameByUuid[source.uuid] = source.name;
                });
            })
        },
        async fetchIntegrationStatusNames(integrationUuid) {
            await httpClient.get(`/integrations/${integrationUuid}/order-statuses`, {limit: 100})
                .then(response => {
                    response.data.data.forEach(status => {
                        this.integrationStatusNameByUuid[status.uuid] = status.value;
                    });
                });
        }
    },
    computed: {
        ...mapGetters({
            project: "route/project",
            profile: "profile/profile"
        }),
        formattedLogs() {
            return this.logs.map(log => {
                const userName = log.user
                    ? log.user.type === 'User'
                        ? `<strong>${log.user.name}</strong>`
                        : `<strong>${this.$t('orderHistory.api')}</strong>`
                    : `<strong>${this.$t('orderHistory.system')}</strong>`;
                let action = '';
                const subject = log.subject.id;

                const properties = this.logProperties(log);
                const before = properties.old ?? {};
                const after = properties.attributes ?? {};
                const attemptParams = { attempt: properties.attempt, max: properties.max_attempts };

                switch (log.event) {
                    case 'order_created':
                        action = this.$t('orderHistory.created_order');
                        break;
                    case 'order_updated':
                        action = this.$t('orderHistory.updated_order');
                        break;
                    case 'integration_order_created':
                        action = this.$t('orderHistory.created_integration_order');
                        break;
                    case 'integration_order_updated':
                        action = this.$t('orderHistory.updated_integration_order');
                        break;
                    case 'integration_order_delivery_number_manual_dispatch':
                        action = this.$t('orderHistory.integration_order_delivery_number_manual_dispatch');
                        break;
                    case 'integration_order_delivery_number_manual_skipped':
                        action = this.$t('orderHistory.integration_order_delivery_number_manual_skipped');
                        break;
                    case 'integration_order_delivery_number_auto_dispatch':
                        action = this.$t('orderHistory.integration_order_delivery_number_auto_dispatch');
                        break;
                    case 'integration_order_delivery_number_auto_skipped':
                        action = this.$t('orderHistory.integration_order_delivery_number_auto_skipped');
                        break;
                    case 'integration_order_delivery_number_attempt_started':
                        action = this.$t('orderHistory.integration_order_delivery_number_attempt_started', attemptParams);
                        break;
                    case 'integration_order_delivery_number_attempt_unsuccessful':
                        action = this.$t('orderHistory.integration_order_delivery_number_attempt_unsuccessful', attemptParams);
                        break;
                    case 'integration_order_delivery_number_attempt_failed':
                        action = this.$t('orderHistory.integration_order_delivery_number_attempt_failed');
                        break;
                    case 'integration_order_delivery_number_attempt_success':
                        action = this.$t('orderHistory.integration_order_delivery_number_attempt_success', attemptParams);
                        break;
                    case 'integration_order_approve_manual_dispatch':
                        action = this.$t('orderHistory.integration_order_approve_manual_dispatch');
                        break;
                    case 'integration_order_approve_manual_skipped':
                        action = this.$t('orderHistory.integration_order_approve_manual_skipped');
                        break;
                    case 'integration_order_approve_auto_dispatch':
                        action = this.$t('orderHistory.integration_order_approve_auto_dispatch');
                        break;
                    case 'integration_order_approve_auto_skipped':
                        action = this.$t('orderHistory.integration_order_approve_auto_skipped');
                        break;
                    case 'integration_order_approve_attempt_started':
                        action = this.$t('orderHistory.integration_order_approve_attempt_started', attemptParams);
                        break;
                    case 'integration_order_approve_attempt_unsuccessful':
                        action = this.$t('orderHistory.integration_order_approve_attempt_unsuccessful', attemptParams);
                        break;
                    case 'integration_order_approve_attempt_failed':
                        action = this.$t('orderHistory.integration_order_approve_attempt_failed');
                        break;
                    case 'integration_order_approve_attempt_success':
                        action = this.$t('orderHistory.integration_order_approve_attempt_success', attemptParams);
                        break;
                    case 'integration_order_resync_manual_dispatch':
                        action = this.$t('orderHistory.integration_order_resync_manual_dispatch');
                        break;
                    case 'integration_order_resync_manual_skipped':
                        action = this.$t('orderHistory.integration_order_resync_manual_skipped');
                        break;
                    case 'integration_order_resync_attempt_started':
                        action = this.$t('orderHistory.integration_order_resync_attempt_started', attemptParams);
                        break;
                    case 'integration_order_resync_attempt_unsuccessful':
                        action = this.$t('orderHistory.integration_order_resync_attempt_unsuccessful', attemptParams);
                        break;
                    case 'integration_order_resync_attempt_failed':
                        action = this.$t('orderHistory.integration_order_resync_attempt_failed');
                        break;
                    case 'integration_order_resync_attempt_success':
                        action = this.$t('orderHistory.integration_order_resync_attempt_success', attemptParams);
                        break;
                    default:
                        action = log.event;
                }

                let title = `${userName} ${action} ${subject}`;

                const skippedEvents = [
                    'integration_order_delivery_number_manual_skipped',
                    'integration_order_delivery_number_auto_skipped',
                    'integration_order_approve_manual_skipped',
                    'integration_order_approve_auto_skipped',
                    'integration_order_resync_manual_skipped',
                ];

                if (skippedEvents.includes(log.event) && properties.message) {
                    const errorTranslationKeys = this.errorTranslationKeys(log.event, ERROR_TRANSLATION_KEYS_BY_EVENT_PREFIX);
                    const translationKey = errorTranslationKeys[properties.message];
                    const reason = translationKey ? this.$t(translationKey) : properties.message;

                    title += `. ${this.$t('orderHistory.fields.reason')} — ${reason}`;
                }

                const attemptFailureEvents = [
                    'integration_order_delivery_number_attempt_unsuccessful',
                    'integration_order_delivery_number_attempt_failed',
                    'integration_order_approve_attempt_unsuccessful',
                    'integration_order_approve_attempt_failed',
                    'integration_order_resync_attempt_unsuccessful',
                    'integration_order_resync_attempt_failed',
                ];

                if (attemptFailureEvents.includes(log.event) && properties.message) {
                    const attemptErrorTranslationKeys = this.errorTranslationKeys(log.event, ATTEMPT_ERROR_TRANSLATION_KEYS_BY_EVENT_PREFIX);
                    const translationKey = attemptErrorTranslationKeys[properties.message];
                    const reason = translationKey ? this.$t(translationKey) : properties.message;
                    const details = properties.details ? ` (${properties.details})` : '';

                    title += `. ${this.$t('orderHistory.fields.reason')} — ${reason}${details}`;

                    if (properties.next_attempt_in_minutes !== undefined && properties.next_attempt_in_minutes !== null) {
                        title += `. ${this.$t('orderHistory.next_attempt_in_minutes', { minutes: properties.next_attempt_in_minutes })}`;
                    }
                }

                const hasChanges = Object.keys(after).length > 0;

                const changesRaw = {};
                if (hasChanges) {
                    if (log.event === 'order_updated') {
                        const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);

                        allKeys.forEach(key => {
                            if (before[key] !== after[key]) {
                                let beforeValue = before[key];
                                let afterValue = after[key];

                                if (key === 'client_uuid') {
                                    beforeValue = this.clientNameByUuid[beforeValue] || beforeValue;
                                    afterValue = this.clientNameByUuid[afterValue] || afterValue;
                                }

                                if (key === 'source_uuid') {
                                    beforeValue = this.sourceNameByUuid[beforeValue] || beforeValue;
                                    afterValue = this.sourceNameByUuid[afterValue] || afterValue;
                                }

                                if (key === 'status_uuid') {
                                    beforeValue = this.statusNameByUuid[beforeValue] || beforeValue;
                                    afterValue = this.statusNameByUuid[afterValue] || afterValue;
                                }

                                if (beforeValue !== undefined && afterValue !== undefined) {
                                    changesRaw[key] = `${beforeValue} → ${afterValue}`;
                                } else if (afterValue !== undefined) {
                                    changesRaw[key] = afterValue;
                                }
                            }
                        });
                    } else if (log.event === 'order_created') {
                        Object.keys(after).forEach(key => {
                            let value = after[key];
                            if (!value) return;

                            if (key === 'client_uuid') {
                                value = this.clientNameByUuid[value] || value;
                            }
                            if (key === 'source_uuid') {
                                value = this.sourceNameByUuid[value] || value;
                            }
                            if (key === 'status_uuid') {
                                value = this.statusNameByUuid[value] || value;
                            }
                            changesRaw[key] = value;
                        });
                    } else if (log.event === 'integration_order_created') {
                        Object.keys(after).forEach(key => {
                            let value = after[key];
                            if (!value) return;

                            if (key === 'status_uuid') {
                                value = this.integrationStatusNameByUuid[value] || value;
                            }

                            const label = key === 'status_uuid' ? 'integration_status_uuid' : key;

                            changesRaw[label] = value;
                        });
                    } else if (log.event === 'integration_order_updated') {
                        Object.keys(after).forEach(key => {
                            let beforeValue = before[key];
                            let afterValue = after[key];

                            if (key === 'status_uuid') {
                                beforeValue = this.integrationStatusNameByUuid[beforeValue] || beforeValue;
                                afterValue = this.integrationStatusNameByUuid[afterValue] || afterValue;
                            }

                            const label = key === 'status_uuid' ? 'integration_status_uuid' : key;

                            if (beforeValue !== undefined && beforeValue !== null && afterValue !== undefined) {
                                changesRaw[label] = `${beforeValue} → ${afterValue}`;
                            } else if (afterValue !== undefined) {
                                changesRaw[label] = afterValue;
                            }
                        });
                    }
                }

                const keyMap = {
                    status_uuid: this.$t('orderHistory.fields.status_uuid'),
                    client_uuid: this.$t('orderHistory.fields.client_uuid'),
                    source_uuid: this.$t('orderHistory.fields.source_uuid'),
                    external_order_number: this.$t('orderHistory.fields.external_order_number'),
                    comment: this.$t('orderHistory.fields.comment'),
                    payment_method: this.$t('orderHistory.fields.payment_method'),
                    delivery_number: this.$t('orderHistory.fields.delivery_number'),
                    integration_status_uuid: this.$t('orderHistory.fields.integration_status_uuid')
                };

                const changesList = Object.keys(changesRaw).map(key => ({
                    label: keyMap[key] || key,
                    value: changesRaw[key]
                }));

                return {
                    ...log,
                    title,
                    hasChanges: changesList.length > 0,
                    changesList
                };
            });
        }
    },
    watch: {
        'profile.id'(id) {
            this.subscribeToNotifications(id);
        }
    },
    mounted() {
        this.fetchLogs();
        this.subscribeToNotifications(this.profile?.id);
    },
    unmounted() {
        this.unsubscribeFromNotifications();
    }
}
</script>
